import { supabase } from '../lib/supabase';

export const taskService = {
  async getAll() {
    const { data, error } = await supabase?.from('ipm_tasks')?.select(`
        *,
        assigned_user:user_profiles!ipm_tasks_assigned_to_fkey(full_name, email),
        created_user:user_profiles!ipm_tasks_created_by_fkey(full_name, email)
      `)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(task => ({
      id: task?.id,
      title: task?.title,
      description: task?.description,
      taskType: task?.task_type,
      priority: task?.priority,
      status: task?.status,
      dueDate: task?.due_date,
      completedDate: task?.completed_date,
      location: task?.location,
      locationName: task?.location_name,
      roomType: task?.room_type,
      instructions: task?.instructions,
      notes: task?.notes,
      completionNotes: task?.completion_notes,
      estimatedDuration: task?.estimated_duration,
      products: task?.products || [],
      equipment: task?.equipment || [],
      assignedTo: task?.assigned_to,
      assignedUser: task?.assigned_user,
      createdBy: task?.created_by,
      createdUser: task?.created_user,
      companyId: task?.company_id,
      createdAt: task?.created_at,
      updatedAt: task?.updated_at
    }));
  },

  async getPerformanceMetrics(filters = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        // Return demo metrics for non-authenticated users
        console.info('User not authenticated, returning demo performance metrics');
        return {
          totalTasks: 24,
          completedTasks: 18,
          completionRate: 75,
          averageDuration: 120, // in minutes
          pendingTasks: 6,
          overdueTasks: 2,
          efficiency: 'good'
        };
      }

      // Get user's company ID
      const { data: employeeData, error: employeeError } = await supabase
        ?.from('company_employees')
        ?.select('company_id')
        ?.eq('user_id', user?.id)
        ?.single();

      if (employeeError || !employeeData?.company_id) {
        console.warn('User not associated with a company, returning demo metrics');
        return {
          totalTasks: 24,
          completedTasks: 18,
          completionRate: 75,
          averageDuration: 120,
          pendingTasks: 6,
          overdueTasks: 2,
          efficiency: 'good'
        };
      }

      // Build query with filters
      let query = supabase
        ?.from('ipm_tasks')
        ?.select('*')
        ?.eq('company_id', employeeData?.company_id);

      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      if (filters?.assignedTo) {
        query = query?.eq('assigned_to', filters?.assignedTo);
      }

      const { data: tasks, error } = await query;

      if (error) {
        console.error('Error fetching performance metrics:', error);
        throw error;
      }

      const totalTasks = tasks?.length || 0;
      const completedTasks = tasks?.filter(task => task?.status === 'completed')?.length || 0;
      const pendingTasks = tasks?.filter(task => task?.status === 'pending' || task?.status === 'assigned')?.length || 0;
      
      const now = new Date();
      const overdueTasks = tasks?.filter(task => 
        task?.due_date && 
        new Date(task.due_date) < now && 
        task?.status !== 'completed'
      )?.length || 0;

      const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
      
      const totalDuration = tasks
        ?.filter(task => task?.estimated_duration && task?.status === 'completed')
        ?.reduce((sum, task) => sum + task?.estimated_duration, 0) || 0;
      
      const completedTasksWithDuration = tasks?.filter(task => 
        task?.estimated_duration && task?.status === 'completed'
      )?.length || 0;

      const averageDuration = completedTasksWithDuration > 0 
        ? Math.round(totalDuration / completedTasksWithDuration) 
        : 0;

      return {
        totalTasks,
        completedTasks,
        pendingTasks,
        overdueTasks,
        completionRate,
        averageDuration, // in minutes
        efficiency: completionRate >= 85 ? 'excellent' : 
                   completionRate >= 70 ? 'good' : 
                   completionRate >= 50 ? 'fair' : 'needs_improvement'
      };

    } catch (error) {
      console.error('Error in getPerformanceMetrics:', error);
      // Return fallback demo data on error
      return {
        totalTasks: 24,
        completedTasks: 18,
        completionRate: 75,
        averageDuration: 120,
        pendingTasks: 6,
        overdueTasks: 2,
        efficiency: 'good'
      };
    }
  },

  async getTasks(filters = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      // Return empty array for non-authenticated users instead of throwing error
      console.info('User not authenticated, returning empty task list');
      return [];
    }

    // Get user's company ID
    const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

    if (!employeeData?.company_id) {
      console.warn('User not associated with a company');
      return [];
    }

    let query = supabase?.from('ipm_tasks')?.select(`*,assigned_user:user_profiles!ipm_tasks_assigned_to_fkey(full_name, email),created_user:user_profiles!ipm_tasks_created_by_fkey(full_name, email)`)?.eq('company_id', employeeData?.company_id);

    // Apply filters
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.priority) {
      query = query?.eq('priority', filters?.priority);
    }
    if (filters?.assignedTo) {
      query = query?.eq('assigned_to', filters?.assignedTo);
    }
    if (filters?.taskType) {
      query = query?.eq('task_type', filters?.taskType);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;
    
    if (error) throw error;
    return data?.map(task => ({
      id: task?.id,
      title: task?.title,
      description: task?.description,
      task_type: task?.task_type,
      priority: task?.priority,
      status: task?.status,
      due_date: task?.due_date,
      completed_date: task?.completed_date,
      location: task?.location,
      location_name: task?.location_name,
      room_type: task?.room_type,
      instructions: task?.instructions,
      notes: task?.notes,
      completion_notes: task?.completion_notes,
      estimated_duration: task?.estimated_duration,
      products: task?.products || [],
      equipment: task?.equipment || [],
      assigned_to: task?.assigned_to,
      assigned_user: task?.assigned_user,
      created_by: task?.created_by,
      created_user: task?.created_user,
      company_id: task?.company_id,
      created_at: task?.created_at,
      updated_at: task?.updated_at
    }));
  },

  async getTasksByDate(date) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      // For non-premium or non-authenticated users, return demo data
      if (!user) {
        console.info('User not authenticated, returning demo task schedule');
        return generateDemoTaskSchedule(date);
      }

      // Get user's company ID
      const { data: employeeData } = await supabase
        ?.from('company_employees')
        ?.select('company_id')
        ?.eq('user_id', user?.id)
        ?.single();

      if (!employeeData?.company_id) {
        console.warn('User not associated with a company, returning demo tasks');
        return generateDemoTaskSchedule(date);
      }

      // Parse the date string to get start and end of the day
      const startOfDay = new Date(date);
      startOfDay?.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(date);
      endOfDay?.setHours(23, 59, 59, 999);

      // Query tasks for the specified date
      const { data: tasks, error } = await supabase
        ?.from('ipm_tasks')
        ?.select(`
          *,
          assigned_user:user_profiles!ipm_tasks_assigned_to_fkey(full_name, email),
          created_user:user_profiles!ipm_tasks_created_by_fkey(full_name, email)
        `)
        ?.eq('company_id', employeeData?.company_id)
        ?.gte('due_date', startOfDay?.toISOString())
        ?.lte('due_date', endOfDay?.toISOString())
        ?.order('due_date', { ascending: true });

      if (error) throw error;

      // Transform tasks with calculated start and end times
      return tasks?.map(task => {
        const dueDate = new Date(task?.due_date);
        const startTime = `${dueDate?.getHours()?.toString()?.padStart(2, '0')}:${dueDate?.getMinutes()?.toString()?.padStart(2, '0')}`;
        
        // Calculate end time based on estimated duration (in minutes)
        const endDate = new Date(dueDate?.getTime() + (task?.estimated_duration || 30) * 60000);
        const endTime = `${endDate?.getHours()?.toString()?.padStart(2, '0')}:${endDate?.getMinutes()?.toString()?.padStart(2, '0')}`;

        return {
          id: task?.id,
          title: task?.title,
          description: task?.description,
          taskType: task?.task_type,
          priority: task?.priority,
          status: task?.status,
          dueDate: task?.due_date,
          startTime,
          endTime,
          completedDate: task?.completed_date,
          location: task?.location,
          locationName: task?.location_name,
          roomType: task?.room_type,
          instructions: task?.instructions,
          notes: task?.notes,
          completionNotes: task?.completion_notes,
          estimatedDuration: task?.estimated_duration,
          products: task?.products || [],
          equipment: task?.equipment || [],
          assignedTo: task?.assigned_to,
          assignedUser: task?.assigned_user,
          createdBy: task?.created_by,
          createdUser: task?.created_user,
          companyId: task?.company_id,
          createdAt: task?.created_at,
          updatedAt: task?.updated_at
        };
      });

    } catch (error) {
      console.error('Error in getTasksByDate:', error);
      // Return demo data on error
      return generateDemoTaskSchedule(date);
    }
  },

  async createTask(taskData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Authentication required to create tasks');

    // Get user's company ID
    const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

    if (!employeeData?.company_id) throw new Error('User not associated with a company. Please contact your administrator.');

    const { data, error } = await supabase?.from('ipm_tasks')?.insert({
        company_id: employeeData?.company_id,
        created_by: user?.id,
        assigned_to: taskData?.assignedTo || user?.id,
        title: taskData?.title,
        description: taskData?.description,
        task_type: taskData?.taskType,
        priority: taskData?.priority || 'medium',
        status: taskData?.status || 'pending',
        due_date: taskData?.dueDate,
        location: taskData?.location,
        location_name: taskData?.locationName,
        room_type: taskData?.roomType || 'grow_room',
        instructions: taskData?.instructions,
        notes: taskData?.notes,
        estimated_duration: taskData?.estimatedDuration,
        products: taskData?.products || [],
        equipment: taskData?.equipment || []
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      title: data?.title,
      description: data?.description,
      taskType: data?.task_type,
      priority: data?.priority,
      status: data?.status,
      dueDate: data?.due_date,
      completedDate: data?.completed_date,
      location: data?.location,
      locationName: data?.location_name,
      roomType: data?.room_type,
      instructions: data?.instructions,
      notes: data?.notes,
      completionNotes: data?.completion_notes,
      estimatedDuration: data?.estimated_duration,
      products: data?.products,
      equipment: data?.equipment,
      assignedTo: data?.assigned_to,
      createdBy: data?.created_by,
      companyId: data?.company_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateTask(id, updates) {
    const { data, error } = await supabase?.from('ipm_tasks')?.update({
        title: updates?.title,
        description: updates?.description,
        task_type: updates?.taskType,
        priority: updates?.priority,
        status: updates?.status,
        due_date: updates?.dueDate,
        completed_date: updates?.completedDate,
        location: updates?.location,
        location_name: updates?.locationName,
        room_type: updates?.roomType,
        instructions: updates?.instructions,
        notes: updates?.notes,
        completion_notes: updates?.completionNotes,
        estimated_duration: updates?.estimatedDuration,
        products: updates?.products,
        equipment: updates?.equipment,
        assigned_to: updates?.assignedTo
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      title: data?.title,
      description: data?.description,
      taskType: data?.task_type,
      priority: data?.priority,
      status: data?.status,
      dueDate: data?.due_date,
      completedDate: data?.completed_date,
      location: data?.location,
      locationName: data?.location_name,
      roomType: data?.room_type,
      instructions: data?.instructions,
      notes: data?.notes,
      completionNotes: data?.completion_notes,
      estimatedDuration: data?.estimated_duration,
      products: data?.products,
      equipment: data?.equipment,
      assignedTo: data?.assigned_to,
      createdBy: data?.created_by,
      companyId: data?.company_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async getCompanyTasks() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get user's company ID
    const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

    if (!employeeData?.company_id) {
      // If user is not in company_employees, return empty array instead of throwing error
      console.warn('User not associated with a company');
      return [];
    }

    const { data, error } = await supabase?.from('ipm_tasks')?.select(`*,assigned_user:user_profiles!ipm_tasks_assigned_to_fkey(full_name, email),created_user:user_profiles!ipm_tasks_created_by_fkey(full_name, email)`)?.eq('company_id', employeeData?.company_id)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(task => ({
      id: task?.id,
      title: task?.title,
      description: task?.description,
      taskType: task?.task_type,
      priority: task?.priority,
      status: task?.status,
      dueDate: task?.due_date,
      completedDate: task?.completed_date,
      location: task?.location,
      locationName: task?.location_name,
      roomType: task?.room_type,
      instructions: task?.instructions,
      notes: task?.notes,
      completionNotes: task?.completion_notes,
      estimatedDuration: task?.estimated_duration,
      products: task?.products || [],
      equipment: task?.equipment || [],
      assignedTo: task?.assigned_to,
      assignedUser: task?.assigned_user,
      createdBy: task?.created_by,
      createdUser: task?.created_user,
      companyId: task?.company_id,
      createdAt: task?.created_at,
      updatedAt: task?.updated_at
    }));
  },

  async getById(id) {
    const { data, error } = await supabase?.from('ipm_tasks')?.select(`*,assigned_user:user_profiles!ipm_tasks_assigned_to_fkey(full_name, email),created_user:user_profiles!ipm_tasks_created_by_fkey(full_name, email)`)?.eq('id', id)?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      title: data?.title,
      description: data?.description,
      taskType: data?.task_type,
      priority: data?.priority,
      status: data?.status,
      dueDate: data?.due_date,
      completedDate: data?.completed_date,
      location: data?.location,
      locationName: data?.location_name,
      roomType: data?.room_type,
      instructions: data?.instructions,
      notes: data?.notes,
      completionNotes: data?.completion_notes,
      estimatedDuration: data?.estimated_duration,
      products: data?.products || [],
      equipment: data?.equipment || [],
      assignedTo: data?.assigned_to,
      assignedUser: data?.assigned_user,
      createdBy: data?.created_by,
      createdUser: data?.created_user,
      companyId: data?.company_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async create(taskData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get user's company ID
    const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

    if (!employeeData) throw new Error('User not associated with a company');

    const { data, error } = await supabase?.from('ipm_tasks')?.insert({
        company_id: employeeData?.company_id,
        created_by: user?.id,
        assigned_to: taskData?.assignedTo || user?.id,
        title: taskData?.title,
        description: taskData?.description,
        task_type: taskData?.taskType,
        priority: taskData?.priority || 'medium',
        status: taskData?.status || 'pending',
        due_date: taskData?.dueDate,
        location: taskData?.location,
        location_name: taskData?.locationName,
        room_type: taskData?.roomType || 'grow_room',
        instructions: taskData?.instructions,
        notes: taskData?.notes,
        estimated_duration: taskData?.estimatedDuration,
        products: taskData?.products || [],
        equipment: taskData?.equipment || []
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      title: data?.title,
      description: data?.description,
      taskType: data?.task_type,
      priority: data?.priority,
      status: data?.status,
      dueDate: data?.due_date,
      completedDate: data?.completed_date,
      location: data?.location,
      locationName: data?.location_name,
      roomType: data?.room_type,
      instructions: data?.instructions,
      notes: data?.notes,
      completionNotes: data?.completion_notes,
      estimatedDuration: data?.estimated_duration,
      products: data?.products,
      equipment: data?.equipment,
      assignedTo: data?.assigned_to,
      createdBy: data?.created_by,
      companyId: data?.company_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async update(id, updates) {
    const { data, error } = await supabase?.from('ipm_tasks')?.update({
        title: updates?.title,
        description: updates?.description,
        task_type: updates?.taskType,
        priority: updates?.priority,
        status: updates?.status,
        due_date: updates?.dueDate,
        completed_date: updates?.completedDate,
        location: updates?.location,
        location_name: updates?.locationName,
        room_type: updates?.roomType,
        instructions: updates?.instructions,
        notes: updates?.notes,
        completion_notes: updates?.completionNotes,
        estimated_duration: updates?.estimatedDuration,
        products: updates?.products,
        equipment: updates?.equipment,
        assigned_to: updates?.assignedTo
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      title: data?.title,
      description: data?.description,
      taskType: data?.task_type,
      priority: data?.priority,
      status: data?.status,
      dueDate: data?.due_date,
      completedDate: data?.completed_date,
      location: data?.location,
      locationName: data?.location_name,
      roomType: data?.room_type,
      instructions: data?.instructions,
      notes: data?.notes,
      completionNotes: data?.completion_notes,
      estimatedDuration: data?.estimated_duration,
      products: data?.products,
      equipment: data?.equipment,
      assignedTo: data?.assigned_to,
      createdBy: data?.created_by,
      companyId: data?.company_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async completeTask(id, completionNotes = '') {
    const { data, error } = await supabase?.from('ipm_tasks')?.update({
        status: 'completed',
        completed_date: new Date()?.toISOString(),
        completion_notes: completionNotes
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      title: data?.title,
      description: data?.description,
      taskType: data?.task_type,
      priority: data?.priority,
      status: data?.status,
      dueDate: data?.due_date,
      completedDate: data?.completed_date,
      location: data?.location,
      locationName: data?.location_name,
      roomType: data?.room_type,
      instructions: data?.instructions,
      notes: data?.notes,
      completionNotes: data?.completion_notes,
      estimatedDuration: data?.estimated_duration,
      products: data?.products,
      equipment: data?.equipment,
      assignedTo: data?.assigned_to,
      createdBy: data?.created_by,
      companyId: data?.company_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async delete(id) {
    const { error } = await supabase?.from('ipm_tasks')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  async getTaskStats() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      // Return zero stats for non-authenticated users instead of throwing error
      console.info('User not authenticated, returning zero stats');
      return {
        total: 0,
        completed: 0,
        pending: 0,
        overdue: 0
      };
    }

    // Get user's company ID
    const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

    if (!employeeData?.company_id) {
      return {
        total: 0,
        completed: 0,
        pending: 0,
        overdue: 0
      };
    }

    const { data, error } = await supabase?.from('ipm_tasks')?.select('status, due_date, completed_date')?.eq('company_id', employeeData?.company_id);
    
    if (error) throw error;

    const now = new Date();
    const stats = {
      total: data?.length || 0,
      completed: data?.filter(task => task?.status === 'completed')?.length || 0,
      pending: data?.filter(task => task?.status === 'pending' || task?.status === 'assigned')?.length || 0,
      overdue: data?.filter(task => 
        task?.due_date && 
        new Date(task.due_date) < now && 
        task?.status !== 'completed'
      )?.length || 0
    };

    return stats;
  },

  async getTeamMembers() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      // Return empty array for non-authenticated users instead of throwing error
      console.info('User not authenticated, returning empty team members list');
      return [];
    }

    // Get user's company ID first
    const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

    if (!employeeData?.company_id) return [];

    // Get all team members from the same company
    const { data, error } = await supabase?.from('company_employees')?.select(`
        user_id,
        user_profiles!company_employees_user_id_fkey(
          id,
          full_name,
          email,
          role
        )
      `)?.eq('company_id', employeeData?.company_id);

    if (error) throw error;
    
    return data?.map(emp => ({
      user_profiles: {
        id: emp?.user_profiles?.id,
        full_name: emp?.user_profiles?.full_name,
        email: emp?.user_profiles?.email,
        role: emp?.user_profiles?.role
      }
    })) || [];
  }
};

// Helper function to generate demo task schedule for non-premium users
function generateDemoTaskSchedule(date) {
  const demoMembers = [
    { id: 'demo-user-1', name: 'Sarah Johnson', position: 'IPM Specialist' },
    { id: 'demo-user-2', name: 'Mike Chen', position: 'Grow Technician' },
    { id: 'demo-user-3', name: 'Emily Davis', position: 'Cultivation Lead' }
  ];

  const demoTasks = [
    {
      id: 'demo-task-1',
      title: 'IPM Scout - Flowering Room',
      description: 'Comprehensive pest scouting in flowering room A',
      taskType: 'monitoring',
      priority: 'high',
      status: 'in_progress',
      dueDate: `${date}T08:00:00`,
      startTime: '08:00',
      endTime: '10:00',
      location: 'Flowering Room A',
      locationName: 'Flowering Room A',
      roomType: 'grow_room',
      estimatedDuration: 120,
      assignedTo: 'demo-user-1',
      products: [],
      equipment: ['Magnifying Glass', 'Sticky Cards', 'Scouting Sheet']
    },
    {
      id: 'demo-task-2',
      title: 'BT Application',
      description: 'Apply Bacillus thuringiensis for caterpillar control',
      taskType: 'treatment_application',
      priority: 'high',
      status: 'scheduled',
      dueDate: `${date}T09:00:00`,
      startTime: '09:00',
      endTime: '10:30',
      location: 'Greenhouse B',
      locationName: 'Greenhouse B',
      roomType: 'greenhouse',
      estimatedDuration: 90,
      assignedTo: 'demo-user-2',
      products: ['Bacillus thuringiensis'],
      equipment: ['Backpack Sprayer', 'PPE Kit']
    },
    {
      id: 'demo-task-3',
      title: 'Beneficial Release',
      description: 'Release predatory mites for spider mite control',
      taskType: 'biological_control',
      priority: 'medium',
      status: 'scheduled',
      dueDate: `${date}T11:00:00`,
      startTime: '11:00',
      endTime: '12:00',
      location: 'Veg Room C',
      locationName: 'Veg Room C',
      roomType: 'grow_room',
      estimatedDuration: 60,
      assignedTo: 'demo-user-1',
      products: ['Neoseiulus californicus'],
      equipment: ['Release Sachets']
    },
    {
      id: 'demo-task-4',
      title: 'Neem Oil Application',
      description: 'Foliar spray with neem oil for preventative care',
      taskType: 'treatment_application',
      priority: 'medium',
      status: 'pending',
      dueDate: `${date}T13:00:00`,
      startTime: '13:00',
      endTime: '14:30',
      location: 'Propagation Area',
      locationName: 'Propagation Area',
      roomType: 'grow_room',
      estimatedDuration: 90,
      assignedTo: 'demo-user-3',
      products: ['Neem Oil', 'Surfactant'],
      equipment: ['Spray Bottle', 'Mixing Container', 'PPE']
    },
    {
      id: 'demo-task-5',
      title: 'Defoliation - Lower Canopy',
      description: 'Remove lower leaves for airflow and pest prevention',
      taskType: 'defoliation',
      priority: 'medium',
      status: 'completed',
      dueDate: `${date}T10:00:00`,
      startTime: '10:00',
      endTime: '11:30',
      location: 'Flowering Room B',
      locationName: 'Flowering Room B',
      roomType: 'grow_room',
      estimatedDuration: 90,
      assignedTo: 'demo-user-2',
      completedDate: `${date}T11:20:00`,
      products: [],
      equipment: ['Pruning Shears', 'Gloves', 'Collection Bags']
    },
    {
      id: 'demo-task-6',
      title: 'Environmental Check',
      description: 'Monitor temperature, humidity, and VPD levels',
      taskType: 'environmental',
      priority: 'high',
      status: 'completed',
      dueDate: `${date}T07:30:00`,
      startTime: '07:30',
      endTime: '08:30',
      location: 'All Rooms',
      locationName: 'All Growing Areas',
      roomType: 'grow_room',
      estimatedDuration: 60,
      assignedTo: 'demo-user-3',
      completedDate: `${date}T08:15:00`,
      products: [],
      equipment: ['Thermometer', 'Hygrometer', 'Data Logger']
    }
  ];

  return demoTasks;
}

export default taskService;